import React, { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import classnames from 'src/util/classnames';
import Icon from 'src/components/Icon';
import { Button } from '../ui';
import SaveStatusPill from 'src/components/SaveStatusPill';

export type EditorToolbarProps = {
  breadcrumb?: ReactNode;
  title: ReactNode;
  isDirty: boolean;
  lastSavedAt: number | null;
  actions?: ReactNode;
  onCancel?: () => void;
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: ReactNode;
  loading?: boolean;
  allowSaveWhenClean?: boolean;
};

/** Record editor header laid out like the form and workflow builders': back · title · status, then actions and Save. */
export function EditorToolbar({
  breadcrumb,
  title,
  isDirty,
  lastSavedAt,
  actions,
  onCancel,
  onSave,
  saving,
  saveLabel,
  loading,
  allowSaveWhenClean,
}: EditorToolbarProps) {
  const backLabel = t`Back`;

  return (
    <header className="shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-0 border-b border-solid border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/50">
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          title={backLabel}
          aria-label={backLabel}
          data-testid="editor-cancel"
          className="w-8 h-8 p-0 inline-flex items-center justify-center rounded border-0 bg-transparent transition-colors cursor-pointer hover:bg-primary/10 theme-text-primary shrink-0 disabled:opacity-30 disabled:cursor-not-allowed! gap-1"
        >
          <Icon icon="ArrowLeft" />
        </button>
      ) : null}

      {/* {breadcrumb ? (
        <>
          <span className="text-xs theme-text-primary opacity-60 shrink-0">{breadcrumb}</span>
          <Icon icon="ChevronRight" className="w-3 h-3 theme-text-primary opacity-40 shrink-0" />
        </>
      ) : null} */}
      <h5 className="m-0 min-w-0 text-sm font-semibold theme-text-primary truncate">{title}</h5>

      <SaveStatusPill isDirty={isDirty} lastSavedAt={lastSavedAt} hasOnSave={!!onSave} loading={loading} />

      <div className="ml-auto flex items-center gap-2">
        {actions}
        {onSave ? (
          <Button
            type="button"
            color="primary"
            className="inline-flex gap-1"
            onClick={onSave}
            disabled={saving || loading || (!isDirty && !allowSaveWhenClean)}
            title={t`Save changes`}
            data-testid="editor-save"
          >
            <Icon icon={saving ? 'CircleNotch' : 'FloppyDisk'} className={classnames({ 'animate-spin': saving })} />
            <span className="hover-label">{saveLabel ?? <Trans>Save</Trans>}</span>
          </Button>
        ) : null}
      </div>
    </header>
  );
}

export default EditorToolbar;
